// src/components/ProfileLanguageDropdown.jsx
// =============================================================================
// Durable profile-language selector for authenticated user menu.
// =============================================================================
//
// PURPOSE
// -------
// This component changes the language preference stored in the authenticated
// user's backend profile. It is meant for the Navbar user dropdown, next to
// account actions such as Change Password and Logout.
//
// IMPORTANT ARCHITECTURAL SPLIT
// -----------------------------
// hb_visits now deliberately separates two language concepts:
//
// 1) Current displayed UI language
//    - owned by i18next runtime state,
//    - changed from the sidebar LanguageSwitcher,
//    - valid only for the current SPA session,
//    - not saved to the backend profile automatically.
//
// 2) Durable profile language
//    - stored in users.preferred_language / user settings in the backend,
//    - used by LoginPage when the user leaves "Profile language" selected,
//    - changed here or from a dedicated user-settings profile screen,
//    - does not automatically overwrite the current visible UI language.
//
// This is intentional. A user may log in with an explicit English session even
// though their saved profile language is Polish, or update the profile language
// for future sessions without disrupting the current screen language.
//
// BACKEND CONTRACT
// ----------------
// The component uses the existing current-user settings endpoint through
// updateMyPreferredLanguage(). No new API endpoint or backend change is needed.
//
// UI POLICY
// ---------
// This selector lives only in the authenticated user menu. It should not be used
// on public pages. Public pages use PublicLanguageDropdown instead.

import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AuthContext from "../context/AuthContext";
import { normalizeLanguage, SUPPORTED_LANGUAGES } from "../i18n";
import { updateMyPreferredLanguage } from "../services/userSettingsService";

const PROFILE_LANGUAGE_OPTIONS = [
  { code: "en", labelKey: "language.english" },
  { code: "pl", labelKey: "language.polish" },
];

function isSupportedLanguage(code) {
  return SUPPORTED_LANGUAGES.includes(code);
}

export default function ProfileLanguageDropdown({ disabled = false }) {
  const { t } = useTranslation("common");
  const { authState, updateCurrentUserLanguage } = useContext(AuthContext);

  const profileLanguage = useMemo(() => {
    return normalizeLanguage(authState?.user?.preferred_language);
  }, [authState?.user?.preferred_language]);

  const [pendingLanguage, setPendingLanguage] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "hint", text: "" });

  const selectedLanguage = pendingLanguage || profileLanguage;

  async function onProfileLanguageChange(event) {
    const nextLanguage = normalizeLanguage(event.target.value);
    if (!isSupportedLanguage(nextLanguage)) return;
    if (saving || nextLanguage === profileLanguage) {
      setPendingLanguage("");
      return;
    }

    setPendingLanguage(nextLanguage);
    setSaving(true);
    setMessage({ type: "hint", text: "" });

    try {
      const saved = await updateMyPreferredLanguage(nextLanguage);
      const savedLanguage = normalizeLanguage(
        saved?.preferred_language || nextLanguage
      );

      /**
       * Update the AuthContext user snapshot so other authenticated widgets can
       * display the latest profile preference immediately.
       *
       * Crucially, applyToRuntime is false. Saving the durable profile language
       * must not replace the current displayed language. The sidebar
       * LanguageSwitcher remains responsible for current-session display.
       */
      updateCurrentUserLanguage?.(savedLanguage, { applyToRuntime: false });

      setPendingLanguage("");
      setMessage({
        type: "success",
        text: t("language.profileSaveSuccess"),
      });
    } catch {
      setPendingLanguage("");
      setMessage({
        type: "error",
        text: t("language.profileSaveError"),
      });
    } finally {
      setSaving(false);
    }
  }

  const helperText =
    message.text ||
    (saving ? t("language.profileSaving") : t("language.profileHint"));

  return (
    <div className="navbar__profileLanguageControl" role="group" aria-label={t("language.profileLabel")}>
      <label className="navbar__profileLanguageLabel" htmlFor="navbar-profile-language-select">
        {t("language.profileLabel")}
      </label>

      <select
        id="navbar-profile-language-select"
        className="navbar__profileLanguageSelect"
        value={selectedLanguage}
        onChange={onProfileLanguageChange}
        disabled={disabled || saving}
        title={t("language.profileSelectTitle")}
      >
        {PROFILE_LANGUAGE_OPTIONS.filter((option) =>
          isSupportedLanguage(option.code)
        ).map((option) => (
          <option key={option.code} value={option.code}>
            {t(option.labelKey)}
          </option>
        ))}
      </select>

      <p
        className={`navbar__profileLanguageHelp navbar__profileLanguageHelp--${message.type}`}
        aria-live="polite"
      >
        {helperText}
      </p>
    </div>
  );
}
